/**
 * The browser half of the comp music to-do batch (the server half, and the
 * whole story, is netlify/functions/lib/compMusicTasks.mjs).
 *
 * Firebase-free on purpose: the Director's preview panel, the To-dos page and
 * the plain node tests all import this, and only TYPES come from offerings.ts
 * (erased before node or the bundler resolve it), the same posture
 * mediaTagging.ts keeps.
 */
import type { MediaLink } from './offerings';
import { publishedMediaLinks } from './mediaTagging.ts';

/** Mirrors COMP_MUSIC_TASK_PREFIX in netlify/functions/lib/compMusicTasks.mjs. */
export const COMP_MUSIC_TASK_PREFIX = 'compMusic_';
/** The batch Bobbie asked for on 2026-09-28; the panel prefills it. */
export const COMP_MUSIC_DEFAULT_DUE_KEY = '2026-10-05';
/** The one button a comp music to-do carries to its class. */
export const COMP_MUSIC_OPEN_LABEL = 'Open Music & Video';
/** Said on the card so nobody hunts for a Mark done that proves nothing. */
export const COMP_MUSIC_SELF_CLEARS =
  'Posting the music checks this off. It closes the next time you open your to-dos after the music is up.';

/** The class's Music & Video tab, the place the upload actually happens. */
export function compMusicClassHref(offeringId: string): string {
  return `/class/${encodeURIComponent(offeringId)}?tab=media`;
}

/**
 * The class a comp music to-do is about, or null for every other to-do. Both
 * halves must agree: the server-owned field AND the id namespace only the
 * server writes, so a hand-typed field on some other to-do never turns it
 * into one of these.
 */
export function compMusicTaskOfferingId(task: {
  id: string;
  compMusicOfferingId?: string | null;
}): string | null {
  if (!task.id.startsWith(COMP_MUSIC_TASK_PREFIX)) return null;
  const offeringId = task.compMusicOfferingId?.trim();
  return offeringId ? offeringId : null;
}

/**
 * Does the To-dos page draw its generic "Mark mine done" button on this row?
 * The same guards Tasks.tsx always had, plus one: NEVER on a comp music
 * to-do. Ticking it by hand would clear the ask with no music posted, and on
 * a Jr Staff's other sign-in the rules refuse the write, so the button would
 * only ever show a refusal there. The upload is what closes it (the server's
 * resolve lane). The rules still accept the assignee's plain completion
 * write, so this screen check is the only guard (zero rules bytes).
 * And NEVER on a monthly award to-do (lib/studentAwardTask.ts, DECISIONS
 * 170): her pick, or No pick this month, is what closes it.
 */
export function markMineDoneShows(row: {
  iAmAssignee: boolean;
  iCompleted: boolean;
  needsUpload: boolean;
  isRoomReq: boolean;
  isNag: boolean;
  compMusicOfferingId: string | null;
  isAwardTodo: boolean;
}): boolean {
  return row.iAmAssignee && !row.iCompleted && !row.needsUpload && !row.isRoomReq && !row.isNag
    && !row.compMusicOfferingId && !row.isAwardTodo;
}

/**
 * THE UPLOAD IS THE ANSWER. True once the dance has any posted music at all:
 * a draft counts, a classroom-tagged track counts, and nothing waits for a
 * final cut, a song name or a costume. `musicLinks` is the normalized list
 * useOfferings hands every screen (normalizeOfferingDoc already runs
 * sortMediaNewestFirst over it); publishedMediaLinks drops an upload still in
 * flight. This is the same count the Attendance reminder reads (PR #1247,
 * src/lib/compMusicNeeds.ts), and tests/comp-music-tasks.test.mjs pins it to
 * the server's own reading on one fixture table.
 */
export function compMusicTaskSatisfied(offering: { musicLinks?: readonly MediaLink[] | null }): boolean {
  return publishedMediaLinks(offering.musicLinks ?? []).length > 0;
}

// ---------------------------------------------------------------------------
// What the server answers. Kept here so the panel and its harness share one
// shape with the client.
// ---------------------------------------------------------------------------

export interface CompMusicRowView {
  taskId: string;
  offeringId: string;
  offeringName: string;
  songName: string | null;
  recipientUid: string;
  recipientName: string;
  jrStaff: boolean;
  signIns: number;
}

export interface CompMusicOmissionView {
  offeringId: string;
  offeringName: string;
  reason: string;
  uid?: string;
  name?: string;
  detail?: string;
}

export interface CompMusicClassOnlyView {
  offeringId: string;
  offeringName: string;
  publishedCount: number;
  teachers: string[];
}

export interface CompMusicPreview {
  ok: true;
  mode: 'preview';
  dueKey: string;
  dueLabel: string;
  senderName: string;
  manifest: { id: string; entries: Array<[string, string]> };
  rows: CompMusicRowView[];
  existing: Array<CompMusicRowView & { completed: boolean }>;
  omissions: CompMusicOmissionView[];
  classOnly: CompMusicClassOnlyView[];
  hasMusic: Array<{ offeringId: string; offeringName: string; publishedCount: number }>;
  counts: {
    offerings: number;
    compDances: number;
    toCreate: number;
    dancesToTell: number;
    existing: number;
    omissions: number;
    classOnly: number;
    hasMusic: number;
  };
}

export interface CompMusicAlertOutcome {
  delivered: number;
  terminal: number;
  retry: number;
  timedOut: number;
  skipped: number;
}

export interface CompMusicRunOutcome {
  taskId: string;
  offeringId: string;
  offeringName: string;
  recipientUid: string;
  recipientName: string;
  outcome: string;
  error?: string;
  alert?: CompMusicAlertOutcome;
}

export interface CompMusicRunCall {
  ok: true;
  mode: 'run';
  dueKey: string;
  manifestId: string;
  outcomes: CompMusicRunOutcome[];
  gone: Array<{ taskId: string; offeringId: string; reason: string }>;
  remaining: number;
  delivery: CompMusicAlertOutcome;
  omissions: CompMusicOmissionView[];
  classOnly: CompMusicClassOnlyView[];
}

export interface CompMusicRunRequest {
  dueKey: string;
  manifestId: string;
  entries: Array<[string, string]>;
  skipTaskIds: string[];
}

/**
 * A refusal from the Function, with the server's code kept. The preview a
 * send is pinned to may be unknown, another Director's, over two hours old
 * or already finished; each of those means "Preview again", never "Finish
 * sending".
 */
export class CompMusicCallError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'CompMusicCallError';
    this.code = code;
  }
}

/** The server codes that close a preview for sending. */
export const COMP_MUSIC_CLOSED_MANIFEST_CODES: readonly string[] = [
  'manifest_unknown', 'manifest_not_yours', 'manifest_expired', 'manifest_finished',
];

/** The refusal a run gives when something new would go out. */
export class CompMusicManifestChangedError extends Error {
  added: Array<{ taskId: string; offeringId: string; offeringName: string; recipientName: string }>;
  constructor(added: CompMusicManifestChangedError['added']) {
    super('manifest_changed');
    this.name = 'CompMusicManifestChangedError';
    this.added = added;
  }
}

/** Plain words for every reason the server can give, in B&B voice. */
export const COMP_MUSIC_REASON_LABELS: Record<string, string> = {
  technique: 'Technique class, never performs',
  demo: 'Demo Studio sample, not a real dance',
  retired: 'Marked archived or retired',
  class_type_not_set: 'Class type not set. It looks like a comp dance only by its name, so set the type on the class to include it',
  no_teacher: 'No teacher listed. Not sent to anyone else',
  no_active_teacher: 'Nobody listed on it can get a to-do',
  jr_staff_dancer_not_promoted: 'Jr Staff dancer sign-in not switched on for staff yet. Promote her on Manage Users, then preview again.',
  sub_seat: 'Temporary sub seat, not a listed teacher',
  inactive_recipient: 'Account paused or not active',
  missing_recipient: 'Account not found',
  same_person: 'Same Jr Staff, her other sign-in. She gets one to-do',
  open_other_batch: 'Already has an open music to-do from another batch',
  existing: 'Already had this to-do',
  id_taken: 'A different to-do already uses this id. It was left alone',
  created_by_this_send: 'Sent earlier in this send and deleted since. Not sent again',
  manifest_closed: 'This preview has been sent or closed. Preview again',
  created: 'To-do created',
  created_earlier_call: 'Created a moment ago in this send',
  resolved_since_preview: 'Music was posted after your preview',
  teacher_changed: 'Teacher changed after your preview',
  teacher_changed_or_inactive: 'Teacher changed or account paused after your preview',
  no_longer_eligible: 'No longer a comp dance that needs music',
  class_removed: 'Class was removed',
  failed: 'Could not be saved',
};

export function compMusicReasonLabel(reason: string): string {
  return COMP_MUSIC_REASON_LABELS[reason] ?? reason;
}
